import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "flex w-full text-sm align-items-baseline justify-content-end mb-s1" }
const _hoisted_2 = { class: "flex w-full align-items-center justify-content-between" }
const _hoisted_3 = { class: "text-xs mt-s1 text-muted align-self-end" }
const _hoisted_4 = {
  key: 1,
  class: "flex align-items-center mt-s2"
}
const _hoisted_5 = {
  for: "unstakeFromFarm",
  class: "text-sm ml-s1"
}

import BigNumber from 'bignumber.js';
import { useI18n } from 'vue-i18n';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { USD_TOKEN } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/common/constants/USD_TOKEN';
import { USD_DECIMAL_PRECISION } from '@/constants/INTERFACE_SETTINGS';
import { ENABLE_FAKE_CARDANO_NETWORK } from '@/helpers/fakeCardanoNetwork';
import { BIG_ZERO } from '@/utils/bigNumber';
import { DEFAULT_CARDANO_CHAIN_ID } from '@/constants/DEFAULT_CARDANO_ID';
import { EasyModeWithdrawForm } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/models/easy-mode-form';
import { formatNumbersMixin } from '@/mixins/format-numbers.mixin';
import {
  minDecimalsFromCardanoAndMilkomeda,
  normalizeAmountByMinDecimals,
} from '@/helpers/milkomeda-wrapped-smartcontract/milkomeda-wsc-calculation';
import PercentButton from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/common/PercentButton.vue';
import InputPanelInfoData from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/withdraw/InputPanelInfoData.vue';
import FormattedInput from '@/components/FormattedInput.vue';
import TokenDropdown from '@/components/token/TokenDropdown.vue';
import Notice from '@/components/Notice.vue';
import LpAmountFormat from '@/components/LpAmountFormat.vue';
import { useBalances } from '@/store/modules/tokens/useBalances';
import { useTokens } from '@/store/modules/tokens/useTokens';
import {
  canUseFarmLP,
  useEasyModeForm,
} from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/composables/useEasyModeForm';
import { useStakedValue } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/composables/useStakedValue';
import { useStore } from 'vuex';


export default /*#__PURE__*/_defineComponent({
  __name: 'WithdrawInputPanel',
  setup(__props) {

const format = formatNumbersMixin.methods.format;

const { easyModeForm: easyModeCommonForm } = useEasyModeForm();
const easyModeForm = easyModeCommonForm as EasyModeWithdrawForm;
const router = useRouter();
const stakedValue = useStakedValue();
const { t } = useI18n();
const { isPresentTokenIntoNetwork } = useTokens();
const { balanceByTokenSymbolAndChainId } = useBalances();
const { state } = useStore();

const currencyOptions = [USD_TOKEN, { symbol: 'LP Tokens', address: 'lp-token' }];

const isBalanceTooltip = computed(() => {
  return (
    ENABLE_FAKE_CARDANO_NETWORK &&
    isPresentTokenIntoNetwork(easyModeForm.lpToken.symbol!, +DEFAULT_CARDANO_CHAIN_ID!)
  );
});
const balanceLPInCardano = computed(() => {
  if (!ENABLE_FAKE_CARDANO_NETWORK) {
    return BIG_ZERO;
  }

  const { symbol } = easyModeForm.lpToken;

  if (!isPresentTokenIntoNetwork(symbol!, +DEFAULT_CARDANO_CHAIN_ID!)) {
    return BIG_ZERO;
  }

  const rawBalance = balanceByTokenSymbolAndChainId(symbol!, +DEFAULT_CARDANO_CHAIN_ID!).value
    ?.balance;

  return normalizeAmountByMinDecimals(
    rawBalance?.toExact() ?? BIG_ZERO.toString(),
    easyModeForm.lpToken,
  );
});
const balanceLPInMilkomeda = computed(() =>
  normalizeAmountByMinDecimals(
    easyModeForm.portfolio.getBalanceOfLp().toString(),
    easyModeForm.lpToken,
  ),
);
const balanceLP = computed(() => balanceLPInCardano.value.plus(balanceLPInMilkomeda.value));
const normalizeStakedValue = computed(() =>
  normalizeAmountByMinDecimals(stakedValue.value.toString(), easyModeForm.lpToken),
);

const focus = ref(false);
const inputValue = ref('');
const selectedCurrency = ref(currencyOptions[0].symbol);
const activePercent = ref<'25' | '50' | '75' | '100' | undefined>(undefined);

const isUSDOptionSelected = computed(() => selectedCurrency.value === currencyOptions[0].symbol);
const inputInLPTokenEquivalent = computed(() => {
  let lpEquivalent = inputValue.value;

  if (isUSDOptionSelected.value) {
    lpEquivalent = BigNumber(inputValue.value || 0)
      .div(easyModeForm.portfolio.getLpTokenPriceUSD())
      .toString();
  }

  return normalizeAmountByMinDecimals(lpEquivalent, easyModeForm.lpToken);
});

const inputInUsdEquivalent = computed(() => {
  return `$${format(
    new BigNumber(inputValue.value || 0)
      .multipliedBy(easyModeForm.portfolio.getLpTokenPriceUSD())
      .toFixed(USD_DECIMAL_PRECISION, BigNumber.ROUND_DOWN),
  )}`;
});

const balanceInSelectedCurrency = computed(() => {
  if (isUSDOptionSelected.value) {
    return BigNumber(
      easyModeForm.portfolio
        .getLpTokenPriceUSD()
        .multipliedBy(balanceLP.value)
        .toFixed(USD_DECIMAL_PRECISION, BigNumber.ROUND_DOWN),
    );
  }

  return normalizeAmountByMinDecimals(balanceLP.value.toString(), easyModeForm.lpToken);
});

const stakedInSelectedCurrency = computed(() => {
  if (isUSDOptionSelected.value) {
    return BigNumber(
      easyModeForm.portfolio
        .getLpTokenPriceUSD()
        .multipliedBy(normalizeStakedValue.value)
        .toFixed(USD_DECIMAL_PRECISION, BigNumber.ROUND_DOWN),
    );
  }

  return normalizeStakedValue.value;
});

const totalBalance = computed(() =>
  easyModeForm.useFarmsLp
    ? stakedInSelectedCurrency.value.plus(balanceInSelectedCurrency.value)
    : balanceInSelectedCurrency.value,
);

const isBalanceTooLow = computed(() => {
  const balance = totalBalance.value;
  return selectedCurrency.value === USD_TOKEN.symbol ? balance.lt(0.01) : balance.isZero();
});

const balanceError = computed(() => {
  return easyModeForm.useFarmsLp
    ? balanceLP.value.plus(normalizeStakedValue.value).lt(inputInLPTokenEquivalent.value)
    : balanceLP.value.lt(inputInLPTokenEquivalent.value);
});

// UNSTAKE FROM THE FARM
const isEnabledUnStakeFromFarm = computed(() =>
  canUseFarmLP(easyModeForm.portfolio, state.farming.lp),
);
// NOTE
// set init value for `useFarmsLp` when user see current panel
easyModeForm.useFarmsLp = canUseFarmLP(easyModeForm.portfolio, state.farming.lp);

/**
 * NOTE: selectedCurrency.value ->
 *       recompute `isUSDOptionSelected` ->
 *       recompute `inputInLPTokenEquivalent` ->
 *       watch(inputInLPTokenEquivalent) ->
 *       set `easyModeForm.lpTokenAmount`
 */
const onSelectCurrency = ({ symbol }: { symbol: string }) => {
  selectedCurrency.value = symbol;
  easyModeForm.selectedCurrencySymbol = symbol;
};

easyModeForm.selectedCurrencySymbol = selectedCurrency.value!;

// NOTE: Changed `LP_TOKEN_DECIMALS` -> easyModeForm.portfolio.lpToken.decimals
const inputDecimals = computed(() => {
  if (isUSDOptionSelected.value) return USD_DECIMAL_PRECISION;

  if (ENABLE_FAKE_CARDANO_NETWORK) {
    return minDecimalsFromCardanoAndMilkomeda(easyModeForm.portfolio.lpToken);
  }

  return easyModeForm.portfolio.lpToken.decimals;
});

const onSetFromPercent = (percent: '25' | '50' | '75' | '100'): void => {
  const balanceValue = totalBalance.value.multipliedBy(percent).div(100);
  inputValue.value = isUSDOptionSelected.value
    ? balanceValue.toFixed(USD_DECIMAL_PRECISION, BigNumber.ROUND_DOWN)
    : normalizeAmountByMinDecimals(balanceValue.toString(), easyModeForm.lpToken).toString();
};

const unstakeClick = () => {
  router.push(
    `${router.resolve({ name: 'Farming' }).path}/${easyModeForm.portfolio.contractAddress}`,
  );
};

watch(inputInLPTokenEquivalent, value => {
  activePercent.value = undefined;
  easyModeForm.lpTokenAmount = new BigNumber(value || 0);
});
watch(balanceError, value => {
  easyModeForm.balanceError = value;
});

watch(
  () => easyModeForm.lpTokenAmount,
  value => {
    if (!value.eq(inputInLPTokenEquivalent.value) && inputValue.value !== '') {
      if (isUSDOptionSelected.value) {
        inputValue.value = value
          .multipliedBy(easyModeForm.portfolio.getLpTokenPriceUSD())
          .toFixed(USD_DECIMAL_PRECISION, BigNumber.ROUND_DOWN);
        return;
      }

      inputValue.value = normalizeAmountByMinDecimals(
        value.toString(),
        easyModeForm.lpToken,
      ).toString();
    }
  },
);

return (_ctx: any,_cache: any) => {
  const _component_Checkbox = _resolveComponent("Checkbox")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(InputPanelInfoData, {
      balance: _unref(totalBalance),
      "farm-staked": _unref(stakedInSelectedCurrency),
      "balance-in-cardano": _unref(balanceLPInCardano),
      "balance-in-milkomeda": _unref(balanceLPInMilkomeda),
      "show-balance-tooltip": _unref(isBalanceTooltip),
      currency: selectedCurrency.value,
      "balance-error": _unref(balanceError),
      onClickUnstake: unstakeClick
    }, null, 8, ["balance", "farm-staked", "balance-in-cardano", "balance-in-milkomeda", "show-balance-tooltip", "currency", "balance-error"]),
    _createElementVNode("div", {
      class: _normalizeClass([{
        'panel-focus': focus.value,
        'panel-error': _unref(balanceError),
      }, "flex p-panel flex-column input-panel justify-content-between mt-s2"])
    }, [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(PercentButton, {
          disabled: _unref(isBalanceTooLow),
          "active-percent": activePercent.value,
          onChange: onSetFromPercent
        }, null, 8, ["disabled", "active-percent"])
      ]),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(TokenDropdown, {
          tokenList: currencyOptions,
          "on-select": onSelectCurrency
        }),
        _createVNode(FormattedInput, {
          modelValue: inputValue.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((inputValue).value = $event)),
          focus: focus.value,
          "onUpdate:focus": _cache[1] || (_cache[1] = ($event: any) => ((focus).value = $event)),
          decimals: _unref(inputDecimals),
          class: "w-full p-0 mt-auto text-right border-none inputtext-lg ml-s2"
        }, null, 8, ["modelValue", "focus", "decimals"])
      ]),
      _createElementVNode("div", _hoisted_3, [
        (_unref(isUSDOptionSelected))
          ? (_openBlock(), _createBlock(LpAmountFormat, {
              key: 0,
              "lp-amount": _unref(inputInLPTokenEquivalent)
            }, null, 8, ["lp-amount"]))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createTextVNode(_toDisplayString(_unref(inputInUsdEquivalent)), 1)
            ], 64))
      ])
    ], 2),
    (_unref(balanceError))
      ? (_openBlock(), _createBlock(Notice, {
          key: 0,
          text: _unref(t)('portfolio.withdraw.decreaseTheAmount'),
          type: "danger",
          class: "mt-s1"
        }, null, 8, ["text"]))
      : _createCommentVNode("", true),
    (_unref(isEnabledUnStakeFromFarm))
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createVNode(_component_Checkbox, {
            modelValue: _unref(easyModeForm).useFarmsLp,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(easyModeForm).useFarmsLp) = $event)),
            binary: true,
            "input-id": "unstakeFromFarm"
          }, null, 8, ["modelValue"]),
          _createElementVNode("label", _hoisted_5, _toDisplayString(_unref(t)('portfolio.easyModeTranslations.withdraw.unstakeFromFarm')), 1)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})